import type { Disposable } from '@patsnap/tahiti-core';
import type { PositioningStrategy } from './positioning-strategies';

/**
 * Renderer for a popover.
 */
export interface PopoverRenderer {
    /**
     * Mount the popover.
     */
    mount(): HTMLElement;
    /**
     * Dispose of the popover.
     */
    dispose(): void;
}

/**
 * Configuration for a popover.
 */
export interface PopoverConfig {
    /**
     * The selector for the popover.
     */
    selector: string;
    /**
     * The render function for the popover.
     * @param reference - The reference element.
     * @param next - The next function.
     */
    render: (reference: HTMLElement, next: () => void) => PopoverRenderer;
    /**
     * The trigger for the popover.
     */
    trigger: 'hover' | 'click';
    /**
     * The positioning strategy for the popover.
     */
    positioningStrategy: PositioningStrategy | ((reference: HTMLElement) => PositioningStrategy);
    /**
     * The hover delay for the popover.
     * @default 0
     */
    hoverDelay?: number;
    /**
     * The hide delay for the popover.
     * @default 200ms
     */
    hideDelay?: number;
    /**
     * The show callback for the popover.
     */
    onShow?: () => void;
    /**
     * The hide callback for the popover.
     */
    onHide?: () => void;
}

/**
 * Registry for popovers.
 */
export interface PopoverRegistry {
    /**
     * Register a popover.
     * @param config - The configuration for the popover.
     */
    register(config: PopoverConfig): Disposable;
    /**
     * Unregister a popover.
     * @param config - The configuration for the popover.
     */
    unregister(config: PopoverConfig): void;
    /**
     * Dispose
     */
    dispose(): void;
}

export const PopoverRegistry = Symbol('PopoverRegistry');
